/**
 * Utility functions for handling API responses consistently
 */

/**
 * Safely extract data from API response
 * @param {Object} response - Axios response object
 * @param {string} dataKey - Key to look for in response (default: 'data')
 * @returns {Array} - Array of data items
 */
export const extractDataFromResponse = (response, dataKey = 'data') => {
  if (!response || !response.data) {
    console.warn('No response or response.data found');
    return [];
  }

  const { data } = response;

  // Handle the specific API response structure: { success: true, data: [...] }
  if (data.success && data.data) {
    if (Array.isArray(data.data)) {
      console.log('Found data in response.data.data (array)');
      return data.data;
    } else if (typeof data.data === 'object' && !Array.isArray(data.data)) {
      // Handle case where data.data is an object with pagination info
      if (data.data.data && Array.isArray(data.data.data)) {
        console.log('Found data in response.data.data.data (paginated array)');
        return data.data.data;
      }
      // Check for other array properties in the nested object
      const nestedKeys = ['jobs', 'blogs', 'posts', 'items', 'results', 'list', 'blogPosts'];
      for (const key of nestedKeys) {
        if (data.data[key] && Array.isArray(data.data[key])) {
          console.log(`Found data in response.data.data.${key}`);
          return data.data[key];
        }
      }
    }
  }

  // Handle flat structure: { success: true, jobs: [...] }
  if (data.success) {
    const flatKeys = ['jobs', 'blogs', 'posts', 'items', 'results', 'list', 'blogPosts'];
    for (const key of flatKeys) {
      if (data[key] && Array.isArray(data[key])) {
        console.log(`Found data in response.data.${key} (flat structure)`);
        return data[key];
      }
    }
  }

  // If data is already an array, return it
  if (Array.isArray(data)) {
    return data;
  }

  // If data has a nested array property, return it
  if (data[dataKey] && Array.isArray(data[dataKey])) {
    return data[dataKey];
  }

  // Handle case where data[dataKey] is an object that might contain an array
  if (data[dataKey] && typeof data[dataKey] === 'object' && !Array.isArray(data[dataKey])) {
    const nestedData = data[dataKey];
    // Check if the nested object has an array property
    if (nestedData.data && Array.isArray(nestedData.data)) {
      console.log(`Found data in '${dataKey}.data' property`);
      return nestedData.data;
    }
    // Check for other common array properties in the nested object
    const nestedKeys = ['jobs', 'blogs', 'posts', 'items', 'results', 'list', 'blogPosts'];
    for (const key of nestedKeys) {
      if (nestedData[key] && Array.isArray(nestedData[key])) {
        console.log(`Found data in '${dataKey}.${key}' property`);
        return nestedData[key];
      }
    }
  }

  // Check for common alternative keys
  const alternativeKeys = ['jobs', 'blogs', 'posts', 'items', 'results'];
  for (const key of alternativeKeys) {
    if (data[key] && Array.isArray(data[key])) {
      console.log(`Found data in '${key}' property`);
      return data[key];
    }
  }

  // If no array found, log the structure and return empty array
  console.warn('Unexpected API response structure:', data);
  console.log('Available keys in response.data:', Object.keys(data));
  
  // Log detailed structure for debugging
  if (data[dataKey]) {
    console.log(`Data in '${dataKey}' key:`, data[dataKey]);
    if (typeof data[dataKey] === 'object' && !Array.isArray(data[dataKey])) {
      console.log(`Keys in '${dataKey}' object:`, Object.keys(data[dataKey]));
    }
  }
  
  return [];
};

/**
 * Process jobs data from API response
 * @param {Array} rawData - Raw jobs data from API
 * @returns {Array} - Processed jobs data
 */
export const processJobsData = (rawData) => {
  if (!Array.isArray(rawData)) {
    console.warn('processJobsData: rawData is not an array:', rawData);
    return [];
  }

  return rawData.map(job => {
    // Handle various date field structures
    const postedDate = job.postedDate || job.createdAt || job.datePosted || new Date().toISOString();
    
    // Handle various ID field structures
    const id = job._id || job.id;
    
    // Handle various company field structures
    const company = job.company || job.hiringOrganization?.name || job.employer?.name || 'Unknown Company';
    
    // Handle various location field structures
    const location = job.location || job.jobLocation?.address?.addressLocality || job.place || 'Location not specified';
    
    // Handle various salary field structures
    const salary = job.salary || job.baseSalary || job.compensation || 'Salary not specified';
    
    // Handle various image field structures
    const images = Array.isArray(job.images) ? job.images : [];
    
    // Use slug from API response (backend generates it)
    const slug = job.slug || '';
    
    return {
      id,
      _id: id,
      title: job.title || 'Untitled Job',
      description: job.description || job.jobDescription || '',
      company,
      location,
      salary,
      postedDate,
      slug,
      images,
      category: job.category || '',
      jobType: job.jobType || '',
      hiringOrganization: job.hiringOrganization || { name: company },
      employmentType: job.employmentType || 'Full-time',
      experienceLevel: job.experienceLevel || 'Entry Level',
      educationLevel: job.educationLevel || 'Any',
      skills: job.skills || [],
      benefits: job.benefits || [],
      requirements: job.requirements || [],
      responsibilities: job.responsibilities || [],
      applicationDeadline: job.applicationDeadline || null,
      isActive: job.isActive !== false, // Default to true unless explicitly false
      status: job.status || 'active'
    };
  });
};

/**
 * Process blogs data from API response
 * @param {Array} rawData - Raw blogs data from API
 * @returns {Array} - Processed blogs data
 */
export const processBlogsData = (rawData) => {
  if (!Array.isArray(rawData)) {
    console.warn('processBlogsData: rawData is not an array:', rawData);
    return [];
  }

  return rawData.map(blog => {
    // Handle various date field structures
    const publicationDate = blog.publicationDate || blog.createdAt || blog.publishedAt || new Date().toISOString();
    
    // Handle various ID field structures
    const id = blog._id || blog.id;
    
    // Handle various author field structures
    const authorName = blog.authorName || blog.author?.name || blog.createdBy?.name || 'Anonymous';
    
    // Handle various category field structures
    const category = blog.category || 'General';
    
    // Use slug from API response (backend generates it)
    const slug = blog.slug || '';
    
    // Handle various status field structures
    const status = blog.status || blog.publishStatus || 'published';
    
    // Handle various image field structures
    const heroImage = blog.heroImage || blog.featuredImage || blog.image || null;
    
    return {
      id,
      _id: id,
      title: blog.title || 'Untitled Blog',
      content: blog.content || blog.body || blog.description || '',
      excerpt: blog.excerpt || blog.summary || '',
      authorName,
      publicationDate,
      updatedAt: blog.updatedAt || blog.modifiedAt || publicationDate,
      category,
      slug,
      status,
      heroImage,
      tags: blog.tags || blog.categories || [],
      readTime: blog.readTime || blog.estimatedReadingTime || '5 min read',
      views: blog.views || blog.viewCount || 0,
      likes: blog.likes || blog.likeCount || 0,
      comments: blog.comments || blog.commentCount || 0,
      isPublished: status === 'published',
      isFeatured: blog.isFeatured || blog.featured || false
    };
  });
};

/**
 * Enhanced slug creation with better handling of special characters
 * @param {string} text - Text to convert to slug
 * @returns {string} - URL-friendly slug
 */
export const createSlug = (text) => {
  if (!text) return '';
  
  return text
    .toString()
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')        // Replace spaces with hyphens
    .replace(/[^\w\-]+/g, '')    // Remove all non-word chars except hyphens
    .replace(/\-\-+/g, '-')      // Replace multiple hyphens with single hyphen
    .replace(/^-+/, '')          // Trim hyphens from start
    .replace(/-+$/, '');         // Trim hyphens from end
};

/**
 * Find item by slug or ID (backend handles slug generation)
 * @param {Array} items - Array of items to search
 * @param {string} identifier - Slug or ID to find
 * @param {string} type - Type of item ('job' or 'blog')
 * @returns {Object|null} - Found item or null
 */
export const findItemByIdentifier = (items, identifier, type = 'job') => {
  if (!Array.isArray(items) || !identifier) {
    return null;
  }
  
  // Strategy 1: Exact matches (ID, slug)
  let found = items.find(item => 
    item.slug === identifier || 
    item._id === identifier || 
    item.id === identifier
  );
  
  if (found) return found;
  
  // Strategy 2: Case-insensitive title match (fallback)
  found = items.find(item => 
    item.title && item.title.toLowerCase() === identifier.toLowerCase()
  );
  
  return found;
};

/**
 * Create old-style slug (shorter, more generic)
 * @param {string} title - Job or blog title
 * @returns {string} - Old-style slug
 */
export const createOldStyleSlug = (title) => {
  if (!title) return '';
  
  // Remove common words that make slugs too long
  const commonWords = [
    'job', 'vacancy', 'opportunity', 'position', 'role', 'career',
    'hiring', 'recruitment', 'employment', 'work', 'at', 'for', 'in',
    'the', 'a', 'an', 'and', 'or', 'but', 'of', 'to', 'with', 'by'
  ];
  
  let slug = title
    .toString()
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^\w\-]+/g, '')
    .replace(/\-\-+/g, '-')
    .replace(/^-+/, '')
    .replace(/-+$/, '');
  
  // Remove common words
  const words = slug.split('-');
  const filteredWords = words.filter(word => 
    word.length > 1 && !commonWords.includes(word)
  );
  
  slug = filteredWords.join('-');
  
  // Limit to 50 characters for old-style compatibility
  if (slug.length > 50) {
    slug = slug.substring(0, 50).replace(/-+$/, '');
  }
  
  return slug;
};

/**
 * Create company + position slug
 * @param {string} title - Job title
 * @param {string} company - Company name
 * @returns {string} - Company + position slug
 */
export const createCompanyPositionSlug = (title, company) => {
  if (!title) return '';
  
  let slug = title
    .toString()
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^\w\-]+/g, '')
    .replace(/\-\-+/g, '-')
    .replace(/^-+/, '')
    .replace(/-+$/, '');
  
  // Add company name if available
  if (company) {
    const companySlug = company
      .toString()
      .toLowerCase()
      .trim()
      .replace(/\s+/g, '-')
      .replace(/[^\w\-]+/g, '')
      .replace(/\-\-+/g, '-')
      .replace(/^-+/, '')
      .replace(/-+$/, '');
    
    slug = `${companySlug}-${slug}`;
  }
  
  // Limit to 75 characters
  if (slug.length > 75) {
    slug = slug.substring(0, 75).replace(/-+$/, '');
  }
  
  return slug;
};

/**
 * Create position-only slug (without company)
 * @param {string} title - Job title
 * @returns {string} - Position-only slug
 */
export const createPositionOnlySlug = (title) => {
  if (!title) return '';
  
  // Remove company names and common words
  const commonWords = [
    'job', 'vacancy', 'opportunity', 'position', 'role', 'career',
    'hiring', 'recruitment', 'employment', 'work', 'at', 'for', 'in',
    'the', 'a', 'an', 'and', 'or', 'but', 'of', 'to', 'with', 'by'
  ];
  
  let slug = title
    .toString()
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^\w\-]+/g, '')
    .replace(/\-\-+/g, '-')
    .replace(/^-+/, '')
    .replace(/-+$/, '');
  
  // Remove common words
  const words = slug.split('-');
  const filteredWords = words.filter(word => 
    word.length > 1 && !commonWords.includes(word)
  );
  
  slug = filteredWords.join('-');
  
  // Limit to 60 characters
  if (slug.length > 60) {
    slug = slug.substring(0, 60).replace(/-+$/, '');
  }
  
  return slug;
};



/**
 * Safe API call wrapper with error handling
 * @param {Function} apiCall - The API call function to execute
 * @param {string} fallbackMessage - Message to show if API fails
 * @returns {Promise<Array>} - Promise that resolves to data array or empty array on error
 */
export const safeApiCall = async (apiCall, fallbackMessage = 'API call failed') => {
  try {
    const response = await apiCall();
    return extractDataFromResponse(response);
  } catch (error) {
    console.error(`${fallbackMessage}:`, error);
    return [];
  }
};

/**
 * Check if API is available and responding
 * @param {string} baseUrl - Base URL to check
 * @returns {Promise<boolean>} - Promise that resolves to true if API is available
 */
export const checkApiHealth = async (baseUrl) => {
  try {
    const response = await fetch(`${baseUrl}/health`, { 
      method: 'GET',
      timeout: 5000 
    });
    return response.ok;
  } catch (error) {
    console.warn('API health check failed:', error);
    return false;
  }
}; 